import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from "@angular/core";
import { AttributePresenceInput, observablize } from "@spscommerce/ng-utils";

import { ModalEvent } from "../../classes/modal-event";
import { ModalAction, ModalActionMethod } from "../../decorators/modal-action";

export enum ModalSize {
    DEFAULT = "default",
    SMALL = "small",
    LARGE = "large"
}

export enum ModalType {
    DEFAULT = "default",
    SUCCESS = "success",
    DELETE = "delete",
    INFO = "info",
    TIP = "tip",
    WARNING = "warning"
}

@Component({
    selector: "sps-modal",
    templateUrl: "./sps-modal.component.html",
    styleUrls: ["./sps-modal.component.scss"],
})
export class SpsModalComponent implements OnDestroy {
    static readonly displayName = "sps-modal";
    static readonly props = {
        actions: "Iterable<ModalActionMethod>",
        title: "string",
        type: "ModalType",
        size: "ModalSize",
        actionRequired: "boolean",
        open: { event: true, type: "ModalEvent" },
        close: { event: true, type: "ModalEvent" },
    };

    @Input()
    get actions(): Iterable<ModalActionMethod> {
        return this._actions;
    }
    set actions(value) {
        if (value) {
            const array = Array.from(value);
            for (let i = 0; i < array.length; i++) {
                const element = array[i];
                if (typeof element === "string") {
                    if (/OK/i.test(element)) {
                        array[i] = <ModalActionMethod>SpsModalComponent.okAction;
                    } else if (/Cancel/i.test(element)) {
                        array[i] = <ModalActionMethod>SpsModalComponent.cancelAction;
                    } else {
                        const descriptor: TypedPropertyDescriptor<any> | void =
                            ModalAction({ label: element })({}, element, { value() {} });
                        if (descriptor) {
                            array[i] = descriptor.get();
                        }
                    }
                }
            }
            this._actions = Object.freeze(array);
        }
    }

    @HostBinding("style.display")
    get displayStyle(): string {
        return this.isOpen ? "block" : "none";
    }
    get isOpen() {
        return this._isOpen;
    }

    get anyActionIsActive() {
        return this._actions.some(action => action.isActive);
    }

    constructor(el: ElementRef, private _cdr: ChangeDetectorRef) {
        if (SpsModalComponent.modals.size === 0) {
            document.addEventListener("click", SpsModalComponent._onDocumentClick);
        }

        this.id = el.nativeElement.hasAttribute("id")
            ? el.nativeElement.getAttribute("id")
            : `SpsModalComponent${SpsModalComponent.idNum++}`;
        SpsModalComponent.modals.set(this.id, this);
    }
    private static idNum = 0;
    static modals = new Map<string, SpsModalComponent>();

    @Input() title = "";
    @Input() type = ModalType.DEFAULT;
    @Input() size = ModalSize.DEFAULT;
    @Input() @AttributePresenceInput() actionRequired: boolean;

    _actions: ReadonlyArray<ModalActionMethod> = Object.freeze([
        <ModalActionMethod>SpsModalComponent.okAction
    ]);

    // tslint:disable no-output-rename
    @Output("open") openEvent = new EventEmitter<ModalEvent>();
    @Output("close") closeEvent = new EventEmitter<ModalEvent>();
    // tslint:enable no-output-rename

    // The semitransparent viewport cover that appears behind the modal dialog box & above the rest of the page
    @ViewChild("cover", { static: false }) cover: ElementRef;

    id: string;

    private _isOpen = false;

    @ModalAction({ label: "OK" })
    static okAction() {}

    @ModalAction({ label: "Cancel" })
    static cancelAction() {}

    static _onDocumentClick(event) {
        if (event && event.target) {
            for (const modal of SpsModalComponent.modals.values()) {
                if (event.target === modal.cover.nativeElement && !modal.actionRequired) {
                    modal.close();
                }
            }
        }
    }

    ngOnDestroy() {
        SpsModalComponent.modals.delete(this.id);

        if (SpsModalComponent.modals.size === 0) {
            document.removeEventListener("click", SpsModalComponent._onDocumentClick);
        }
    }

    open() {
        if (!this._isOpen) {
            this._isOpen = true;
            this.openEvent.emit(new ModalEvent("open"));
        }
    }

    close(actionThatsClosingIt?: ModalActionMethod) {
        if (this._isOpen) {
            this._isOpen = false;
            this.closeEvent.emit(new ModalEvent("close", { action: actionThatsClosingIt }));
            this._cdr.markForCheck();
        }
    }

    toggle() {
        if (this._isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    doAction(action: ModalActionMethod) {
        if (this._actions.indexOf(action) > -1 && action.enabled && !action.isActive) {
            const wasActionRequired = this.actionRequired;
            this.actionRequired = true; // This will prevent the user from closing the modal while the action is active
            action.isActive = true;

            try {
                observablize(action()).subscribe((closeModal: boolean) => {
                    action.isActive = false;
                    this.actionRequired = wasActionRequired;
                    if (closeModal !== false) {
                        this.close(action);
                    }
                });
            } catch (err) {
                action.isActive = false;
                this.actionRequired = wasActionRequired;
                console.log("Error executing modal action:", err);
            }
        }
    }

    @HostListener("document:keyup.esc", ["$event"])
    _closeWithoutAction(event?: Event) {
        if (this._isOpen && !this.actionRequired) {
            event.stopPropagation();
            event.preventDefault();
            this.close();
        }
    }
}
